import { AuthUser, OrganizationRole } from '@/features/authentication/contexts';
import { apiClient } from '@/services/clients/axiosClient';
import { APP_SUBDOMAIN, getSubdomain } from '@/utils/subdomainUtils';

export interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

/**
 * Normalize and constrain returnUrl to relative paths only.
 */
const safeReturnUrl = (rawReturnUrl: string): string => {
  if (!rawReturnUrl) return '/';
  if (rawReturnUrl.startsWith('/')) return rawReturnUrl;
  return '/';
};

/**
 * Initiate Cognito OAuth login (B2C users on organization subdomains)
 * Redirects to Cognito hosted UI
 */
const cognitoLogin = (returnUrl: string): void => {
  window.location.href = `/api/auth/cognito/login?returnUrl=${encodeURIComponent(returnUrl)}`;
};

/**
 * Initiate OIDC SSO login (B2B admin users on app subdomain)
 * Redirects to OIDC provider (socialassurance, classintercom, etc.)
 * @param returnUrl - URL to return to after authentication
 * @param provider - OIDC provider identifier (defaults to 'socialassurance')
 */
const oidcLogin = (
  returnUrl: string,
  provider: string = 'socialassurance'
): void => {
  window.location.href = `/api/auth/oidc/login?returnUrl=${encodeURIComponent(returnUrl)}&provider=${provider}`;
};

/**
 * Public API for authentication
 */
export const authApi = {
  /**
   * Initiate login - app subdomain goes to provider selection, others go to Cognito
   * @param rawReturnUrl - URL to return to after authentication
   */
  login: (rawReturnUrl: string): void => {
    const returnUrl = safeReturnUrl(rawReturnUrl);
    const sub = getSubdomain();
    if (sub === APP_SUBDOMAIN) {
      window.location.href = `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
    } else {
      cognitoLogin(returnUrl);
    }
  },

  /**
   * Initiate OIDC login with specific provider (for app subdomain)
   */
  oidcLogin: (
    rawReturnUrl: string,
    provider: string = 'socialassurance'
  ): void => {
    const returnUrl = safeReturnUrl(rawReturnUrl);
    oidcLogin(returnUrl, provider);
  },

  /**
   * Initiate Cognito login (for organization subdomains)
   */
  cognitoLogin: (rawReturnUrl: string): void => {
    const returnUrl = safeReturnUrl(rawReturnUrl);
    cognitoLogin(returnUrl);
  },

  /**
   * Logout - backend determines the appropriate logout method based on session/cookies
   */
  logout: async (): Promise<string> => {
    const response = await apiClient.post<{ logoutUrl: string }>(
      '/auth/logout'
    );
    return response.logoutUrl;
  },

  /**
   * Refresh tokens (cookie-based). Returns success boolean.
   */
  refresh: async (): Promise<boolean> => {
    try {
      await apiClient.post('/auth/refresh', {});
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Get current user session info from JWT token.
   * Used to restore session on page refresh.
   */
  me: async (): Promise<AuthUser | null> => {
    try {
      const response = await apiClient.get<{
        organizations: OrganizationRole[];
        user?: {
          id: string;
          email: string;
          firstName?: string | null;
          lastName?: string | null;
        };
      }>('/auth/me');
      if (response.organizations?.length > 0) {
        return {
          organizations: response.organizations,
          ...(response.user && { user: response.user }),
        };
      }
      return null;
    } catch {
      return null;
    }
  },
};
